import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge, OccurrenceBadges } from "@/components/StatusUI";
import { STATUS, fmtDate } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle, Loader2, Filter, Search, X } from "lucide-react";

export default function Pedidos() {
  const [pedidos, setPedidos] = useState(null);
  const [units, setUnits] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtro, setFiltro] = useState(searchParams.get("status") || "");
  const [ativos, setAtivos] = useState(searchParams.get("ativos") === "1");
  const [unitId, setUnitId] = useState(searchParams.get("unit_id") || "");
  const [busca, setBusca] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE = 100;
  const nav = useNavigate();

  useEffect(() => { api.get("/units").then((r) => setUnits(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setFiltro(searchParams.get("status") || "");
    setAtivos(searchParams.get("ativos") === "1");
    setUnitId(searchParams.get("unit_id") || "");
  }, [searchParams]);

  const buildQ = useCallback((off) => {
    const params = [`limit=${PAGE}`, `offset=${off}`];
    if (filtro) params.push(`status=${filtro}`);
    else if (ativos) params.push("ativos=1");
    if (unitId) params.push(`unit_id=${unitId}`);
    return `?${params.join("&")}`;
  }, [filtro, ativos, unitId]);
  const load = useCallback(() => {
    setOffset(0);
    api.get(`/pedidos${buildQ(0)}`).then((r) => { setPedidos(r.data); setHasMore(r.data.length === PAGE); });
  }, [buildQ]);
  const carregarMais = () => {
    const next = offset + PAGE;
    setLoadingMore(true);
    api.get(`/pedidos${buildQ(next)}`).then((r) => {
      setPedidos((prev) => [...(prev || []), ...r.data]);
      setOffset(next); setHasMore(r.data.length === PAGE);
    }).finally(() => setLoadingMore(false));
  };
  useEffect(() => { load(); }, [load]);

  const selectStatus = (st) => { setFiltro(st); setAtivos(false); };
  const limparDrill = () => { setAtivos(false); setUnitId(""); setSearchParams({}, { replace: true }); };
  const unidadeNome = unitId ? (units.find((u) => u.id === unitId) || {}).codigo : null;
  const drillAtivo = ativos || !!unitId;

  const filtrados = useMemo(() => {
    if (!pedidos) return null;
    const b = busca.trim().toLowerCase();
    return b ? pedidos.filter((p) => p.numero.toLowerCase().includes(b)) : pedidos;
  }, [pedidos, busca]);

  return (
    <div className="space-y-4 sm:space-y-5" data-testid="pedidos-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] sm:text-xs text-primary uppercase tracking-[0.18em] font-bold">Fluxo Operacional</div>
          <h1 className="font-display text-2xl sm:text-4xl font-extrabold uppercase">Pedidos</h1>
        </div>
        <Button onClick={() => nav("/pedidos/novo")} data-testid="btn-novo-pedido" className="shrink-0">
          <PlusCircle className="w-4 h-4" /> <span className="hidden sm:inline">Novo Pedido</span><span className="sm:hidden">Novo</span>
        </Button>
      </div>

      {/* Busca por número */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por número do pedido…"
          className="pl-9" data-testid="pedidos-busca" />
      </div>

      {/* Banner de drill-down (veio de um card do Painel) */}
      {drillAtivo && (
        <div className="flex items-center gap-2 flex-wrap rounded-md border border-primary/50 bg-primary/10 px-3 py-2 text-sm" data-testid="pedidos-drill-banner">
          <span className="text-xs uppercase tracking-wide text-primary font-bold">Filtro do Painel:</span>
          {ativos && <span className="rounded-sm bg-primary/20 border border-primary/40 px-2 py-0.5 text-xs font-mono">Pedidos ativos</span>}
          {unidadeNome && <span className="rounded-sm bg-primary/20 border border-primary/40 px-2 py-0.5 text-xs font-mono">Unidade {unidadeNome}</span>}
          <button onClick={limparDrill} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="pedidos-drill-limpar">
            <X className="w-3.5 h-3.5" /> Limpar
          </button>
        </div>
      )}

      {/* Filtros mobile: Select compacto */}
      <div className="lg:hidden flex items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
        <Select value={filtro || "TODOS"} onValueChange={(v) => selectStatus(v === "TODOS" ? "" : v)}>
          <SelectTrigger className="flex-1" data-testid="filtro-status-select">
            <SelectValue placeholder="Status: Todos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="TODOS">Todos os status</SelectItem>
            {Object.keys(STATUS).map((st) => <SelectItem key={st} value={st}>{STATUS[st].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Filtros desktop: chips */}
      <div className="hidden lg:flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
        <Button variant={filtro === "" ? "default" : "outline"} size="sm" onClick={() => selectStatus("")} data-testid="filtro-todos">Todos</Button>
        {Object.keys(STATUS).map((st) => (
          <Button key={st} variant={filtro === st ? "default" : "outline"} size="sm" onClick={() => selectStatus(st)} data-testid={`filtro-${st}`}>
            {STATUS[st].label}
          </Button>
        ))}
      </div>

      {!filtrados ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtrados.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">Nenhum pedido encontrado.</Card>
      ) : (
        <div className="grid gap-2.5 sm:gap-3">
          {filtrados.map((p) => (
            <Card key={p.id} onClick={() => nav(`/pedidos/${p.id}`)} data-testid={`pedido-card-${p.numero}`}
              className="bg-card/70 p-3 sm:p-4 cursor-pointer hover:border-primary/50 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-base sm:text-lg">{p.numero}</span>
                    {p.tipo === "COMPLEMENTAR" && <span className="text-[10px] uppercase font-bold bg-amber-500/20 text-amber-200 border border-amber-500/50 px-1.5 py-0.5 rounded-sm font-mono">Complementar</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">{p.itens.length} itens · {fmtDate(p.created_at)}</div>
                  <OccurrenceBadges tipos={p.ocorrencias_ativas} />
                </div>
                <StatusBadge status={p.status} />
              </div>
            </Card>
          ))}
          {hasMore && !busca && (
            <div className="flex justify-center pt-2">
              <Button variant="outline" onClick={carregarMais} disabled={loadingMore} data-testid="btn-carregar-mais">
                {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : "Carregar mais"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
